import moment from 'moment'
import stringToHslColor from '@jimmyboys/liita-ui/dist/utils/stringToHslColor'
import { i18n } from 'localization'
import { parseDate, HSLToHex } from './utils'

export default class MedicalSpecialtyDisability {
	static fromJSON(json) {
		return new MedicalSpecialtyDisability(
			json.medicalSpecialty.id,
			json.medicalSpecialty.name
		).withHistory(json.disabilityHistory)
	}

	static computeHistoryStats(disabilityHistory) {
		const aggregatedKeys = []
		const aggregatedKeysObject = {}
		const intervalDays = {}
		let maxDate
		let minDate
		;(disabilityHistory || []).forEach((group) => {
			aggregatedKeys.push([group.name, group.color])
			aggregatedKeysObject[group.name] = 0
			;(group?.history || []).forEach((historyEntry) => {
				if (!historyEntry.startDate) return
				const startDate = historyEntry.startDate.startOfDay
				const endDate =
					historyEntry.endDate && historyEntry.endDate.startOfDay
						? historyEntry.endDate.startOfDay
						: historyEntry.startDate.nextDay

				if (!minDate || startDate.valueOf() < minDate.valueOf()) {
					minDate = startDate
				}
				if (!maxDate || endDate.valueOf() > maxDate.valueOf()) {
					maxDate = endDate
				}

				const diff = endDate.diff(startDate, 'days')
				for (let i = 0; i <= diff; i++) {
					const date = startDate
						.clone()
						.add(i, 'day')
						.format('DD-MM-YY')
					if (!intervalDays[date]) {
						intervalDays[date] = {
							rate: historyEntry.rate,
							[group.name]: historyEntry.rate,
						}
					} else {
						intervalDays[date] = {
							...intervalDays[date],
							rate: Math.max(
								intervalDays[date].rate,
								historyEntry.rate
							),
							[group.name]: Math.max(
								intervalDays[date][group.name] || 0,
								historyEntry.rate
							),
						}
					}
				}
			})
		})
		const perDay = []
		const totalDays = maxDate?.diff(minDate, 'days')
		let partialDisabilityDays = 0
		let totalDisabilityDays = 0

		for (let i = 0; i <= totalDays; i++) {
			const currentDate = minDate.clone().add(i, 'day')
			const date = currentDate.format('DD-MM-YY')
			const dateData = intervalDays[date]
			if (dateData) {
				if (dateData.rate > 0 && dateData.rate < 100) {
					partialDisabilityDays += 1
				} else if (dateData.rate > 0) {
					totalDisabilityDays += 1
				}
				perDay.push({
					...aggregatedKeysObject,
					...dateData,
					date: currentDate.toDate(),
				})
			} else {
				perDay.push({
					date: currentDate.toDate(),
					...aggregatedKeysObject,
				})
			}
		}

		return {
			minDate: minDate?.toDate(),
			maxDate: maxDate?.toDate(),
			categories: aggregatedKeys,
			partialDisabilityDays,
			totalDisabilityDays,
			perDay,
			grouped: disabilityHistory.sort((a, b) => {
				const aLastTimestamp =
					a.history[a.history.length - 1].endDate?.timestamp ||
					a.history[a.history.length - 1].startDate?.timestamp ||
					Date.now()
				const bLastTimestamp =
					b.history[b.history.length - 1].endDate?.timestamp ||
					b.history[b.history.length - 1].startDate?.timestamp ||
					Date.now()
				return bLastTimestamp - aLastTimestamp
			}),
		}
	}

	constructor(id, name) {
		this.id = id
		this.key = `medical_specialty_${id || ''}`
		this.name = name ? i18n.translate(name.trim()) : ''
		this.color = HSLToHex(stringToHslColor(`${id}`, 50, 60))
	}

	withHistory(json) {
		this.numberOfTPDDays = 0
		this.numberOfTTDDays = 0
		this.history = Array.isArray(json)
			? json.map((entry) => {
					const rate = parseInt(entry.disabilityRate, 10) || 0
					const now = moment()
					const startDate = parseDate(entry.startDate)
					const endDate = entry.endDate
						? parseDate(entry.endDate)
						: undefined
					const deltaDays = (endDate?.momentDate || now).diff(
						startDate?.momentDate || now,
						'days'
					)
					if (
						!this.startDate ||
						this.startDate.timestamp >
							(startDate?.timestamp || new Date().getTime())
					) {
						this.startDate = startDate
					}
					if (
						!this.endDate ||
						this.endDate.timestamp < (endDate?.timestamp || 0)
					) {
						this.endDate = endDate
					}

					if (rate === 100) {
						// TEMPORARY TOTAL DISABILITY
						this.numberOfTTDDays += deltaDays
					} else if (rate > 0) {
						// TEMPORARY PARTIAL DISABILITY
						this.numberOfTPDDays += deltaDays
					}

					return {
						startDate,
						endDate,
						deltaDays,
						rate,
						type: entry.type
							? {
									id: entry.type.id,
									name: i18n.translate(
										(entry.type.name || '').trim()
									),
							  }
							: undefined,
					}
			  })
			: []
		return this
	}
}
